// src/pages/Admin/ReglementInscription/components/ReglementsTable.jsx

import React from "react";
import { FaCreditCard, FaEdit, FaFilePdf, FaTrash } from "react-icons/fa";

import {
  calculateReglementTotal,
  fmtEUR,
  getAdherentName,
  getReglementId,
  getStatusDisplay,
  STATUTS_REGLEMENT,
} from "../reglement.helpers";

function sortIcon(order) {
  return order === "asc" ? "▲" : "▼";
}

export default function ReglementsTable({
  rows,
  totalCount,
  visibleCount,
  loading,
  scrollRef,
  onScroll,
  sortField,
  sortOrder,
  toggleNameSort,
  toggleTotalSort,
  editingStatusId,
  setEditingStatusId,
  statusDraft,
  setStatusDraft,
  savingStatusId,
  saveStatus,
  onEdit,
  onDelete,
  onAttestation,
  onOnlinePayment,
  clubSettings,
}) {
  return (
    <div className="p-3 sm:p-5 lg:p-6">
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="max-h-[72vh] overflow-auto rounded-[24px] border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950/60"
      >
        <table className="min-w-[1320px] w-full text-sm">
          <thead className="sticky top-0 z-30 bg-slate-50 dark:bg-slate-900">
            <tr className="text-left">
              <th className="sticky left-0 z-40 w-[260px] bg-slate-50 px-4 py-3 font-bold text-slate-700 dark:bg-slate-900 dark:text-slate-200">
                <button
                  type="button"
                  onClick={toggleNameSort}
                  className="inline-flex items-center gap-2 rounded-xl px-2 py-1 transition hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  Adhérent
                  <span>
                    {sortField === "name" ? sortIcon(sortOrder) : "⇅"}
                  </span>
                </button>
              </th>

              <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                Statut
              </th>

              <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                Saison / Groupes
              </th>

              <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                Montants
              </th>

              <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                <button
                  type="button"
                  onClick={toggleTotalSort}
                  className="inline-flex items-center gap-2 rounded-xl px-2 py-1 transition hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  Total
                  <span>
                    {sortField === "total" ? sortIcon(sortOrder) : "⇅"}
                  </span>
                </button>
              </th>

              <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                Paiement
              </th>

              <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                Observations
              </th>

              <th className="sticky right-0 z-40 w-[190px] bg-slate-50 px-4 py-3 text-right font-bold text-slate-700 dark:bg-slate-900 dark:text-slate-200">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {loading ? (
              [...Array(8)].map((_, index) => (
                <tr key={index}>
                  <td colSpan={8} className="px-4 py-3">
                    <div className="h-10 animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800" />
                  </td>
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-4 py-10 text-center text-slate-500 dark:text-slate-400"
                >
                  Aucun règlement ne correspond aux filtres.
                </td>
              </tr>
            ) : (
              rows.map((reglement) => {
                const id = getReglementId(reglement);
                const adherent = reglement?.Adherent || {};
                const status = getStatusDisplay(reglement?.statut_reglement);
                const total = calculateReglementTotal(reglement);
                const isEditing = String(editingStatusId) === String(id);

                return (
                  <tr
                    key={id}
                    className="transition hover:bg-slate-50 dark:hover:bg-slate-900"
                  >
                    <td className="sticky left-0 z-20 bg-white px-4 py-3 dark:bg-slate-950">
                      <div className="font-bold text-slate-900 dark:text-white">
                        {getAdherentName(reglement)}
                      </div>

                      {/* <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                        ID règlement {id} · ID adhérent {adherent?.id || "—"}
                      </div> */}
                    </td>

                    <td className="px-4 py-3">
                      {isEditing ? (
                        <select
                          autoFocus
                          value={statusDraft || ""}
                          disabled={String(savingStatusId) === String(id)}
                          onChange={(event) => {
                            const value = event.target.value;
                            setStatusDraft(value);
                            saveStatus(reglement, value);
                          }}
                          onBlur={() => setEditingStatusId(null)}
                          className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                        >
                          <option value="">—</option>
                          {STATUTS_REGLEMENT.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setEditingStatusId(id);
                            setStatusDraft(reglement?.statut_reglement || "");
                          }}
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ring-1 ${status.className}`}
                        >
                          {String(savingStatusId) === String(id)
                            ? "..."
                            : status.label}
                        </button>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      <div className="font-bold text-slate-900 dark:text-white">
                        {reglement?.saison || "—"}
                      </div>

                      <div className="mt-2 flex max-w-[250px] flex-wrap gap-1">
                        {(adherent?.Groups || []).length > 0 ? (
                          adherent.Groups.slice(0, 4).map((group) => (
                            <span
                              key={group.id || group.nom}
                              className=" bg-indigo-50 px-2 py-1 text-[11px] font-bold text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20"
                            >
                              {group.nom}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-slate-400">
                            Aucun groupe
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      <div className="w-[240px] space-y-1 text-xs text-slate-700 dark:text-slate-300">
                        <div className="flex justify-between gap-3">
                          <span>Lic. + ass.</span>
                          <strong>
                            {fmtEUR(reglement?.montant_lic_assurance)}
                          </strong>
                        </div>

                        <div className="flex justify-between gap-3">
                          <span>Montant Adh</span>
                          <strong>{fmtEUR(reglement?.montant_adhesion)}</strong>
                        </div>

                        <div className="flex justify-between gap-3">
                          <span>Cotisation</span>
                          <strong>
                            {fmtEUR(reglement?.montant_cotisation)}
                          </strong>
                        </div>

                        <div className="flex justify-between gap-3">
                          <span>Réduc. foyer</span>
                          <strong>{fmtEUR(reglement?.reduction_foyer)}</strong>
                        </div>

                        <div className="flex justify-between gap-3">
                          <span>Pass'Sport</span>
                          <strong>{fmtEUR(reglement?.pass_sport)}</strong>
                        </div>

                        <div className="flex justify-between gap-3">
                          <span>Réduc. saison</span>
                          <strong>{fmtEUR(reglement?.reduction_saison)}</strong>
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      <div className="text-base font-extrabold text-slate-900 dark:text-white">
                        {fmtEUR(total)}
                      </div>
                    </td>

                    <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                      <div className="font-bold">
                        {reglement?.type_reglement || "—"}
                      </div>
                      <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                        {reglement?.nb_reglement ?? "—"} règlement(s)
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      <div className="max-w-[260px] truncate text-slate-700 dark:text-slate-300">
                        {reglement?.observations || "—"}
                      </div>
                    </td>

                    <td className="sticky right-0 z-20 bg-white px-4 py-3 dark:bg-slate-950">
                      <div className="flex justify-end gap-2">
                        {/* {reglement?.type_reglement === "En ligne" ? (
                          <button
                            type="button"
                            onClick={() => onOnlinePayment(reglement)}
                            className="flex h-9 w-9 items-center justify-center rounded-2xl border border-slate-200 bg-white text-emerald-600 transition hover:bg-emerald-50 dark:border-slate-700 dark:bg-slate-900 dark:text-emerald-300 dark:hover:bg-emerald-500/10"
                            title="Paiement en ligne"
                          >
                            <FaCreditCard />
                          </button>
                        ) : null} */}

                        <button
                          type="button"
                          onClick={() => onAttestation(reglement)}
                          disabled={!clubSettings}
                          className="flex h-9 w-9 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                          title="Attestation"
                        >
                          <FaFilePdf />
                        </button>

                        <button
                          type="button"
                          onClick={() => onEdit(reglement)}
                          className="flex h-9 w-9 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                          title="Modifier"
                        >
                          <FaEdit />
                        </button>

                        <button
                          type="button"
                          onClick={() => onDelete(id)}
                          className="flex h-9 w-9 items-center justify-center rounded-2xl bg-rose-600 text-white transition hover:bg-rose-700"
                          title="Supprimer"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {!loading && visibleCount < totalCount ? (
          <div className="border-t border-slate-200 p-3 text-center text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
            Affichage de {visibleCount} / {totalCount} — descendez pour charger
            la suite.
          </div>
        ) : null}
      </div>
    </div>
  );
}
